import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Package, Ruler, PhoneCall, CalendarClock, PencilLine, ArrowRight, ChevronDown, BadgeDollarSign } from "lucide-react";
import { PageHero, SectionHeading, reveal } from "@/components/site/primitives";
import Seo from "@/components/site/Seo";
import { CTABand } from "@/pages/BusinessWaste";
import { CONTACT } from "@/lib/data";
import { useContent } from "@/i18n/content";
import { useI18n } from "@/i18n";

const STEP_ICONS = [PhoneCall, CalendarClock, PencilLine];

export default function DumpsterDetail({ variant = "front" }) {
  const { t, localePath } = useI18n();
  const content = useContent();
  const { size } = useParams();
  const [openFaq, setOpenFaq] = useState(0);

  const isRoll = variant === "roll";
  const items = isRoll ? content.ROLLOFF_DETAILS : content.DUMPSTER_DETAILS;
  const basePath = isRoll ? "/dumpster-rentals" : "/business-waste-service";
  const crumbBase = isRoll ? t("dr.crumb") : t("bw.crumb");
  const eyebrow = isRoll ? t("dd.eyebrowRoll") : t("dd.eyebrowFront");
  const PROCESS_STEPS = content.PROCESS_STEPS;

  const d = items.find((x) => x.slug === size);
  if (!d) return <Navigate to={localePath(basePath)} replace />;
  const others = items.filter((x) => x.slug !== d.slug);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: d.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const priceValue = Number(String(d.priceFrom).replace(/[^0-9.]/g, ""));
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${d.title} in Miami-Dade`,
    serviceType: isRoll ? "Roll-off dumpster rental" : "Commercial front-load dumpster service",
    provider: { "@id": "https://eewaste.com/#business" },
    areaServed: { "@type": "AdministrativeArea", name: "Miami-Dade County, Florida" },
    description: d.seoDesc,
    url: `https://eewaste.com${basePath}/${d.slug}`,
    offers: Number.isFinite(priceValue) ? {
      "@type": "Offer",
      priceCurrency: "USD",
      price: priceValue,
      description: "Starting price",
      availability: "https://schema.org/InStock",
    } : undefined,
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav.home"), item: "https://eewaste.com/" },
      { "@type": "ListItem", position: 2, name: crumbBase, item: `https://eewaste.com${basePath}` },
      { "@type": "ListItem", position: 3, name: d.title, item: `https://eewaste.com${basePath}/${d.slug}` },
    ],
  };
  const jsonLd = [faqJsonLd, serviceJsonLd, breadcrumbJsonLd];

  return (
    <>
      <Seo title={d.seoTitle} description={d.seoDesc} image={d.img} path={`${basePath}/${d.slug}`} jsonLd={jsonLd} />

      <PageHero
        crumb={`${crumbBase} · ${d.title}`}
        eyebrow={eyebrow}
        title={d.title}
        highlight={t("dd.rentalsWord")}
        subtitle={d.intro}
      >
        <div className="flex flex-wrap items-center gap-5">
          <div className="rounded-2xl bg-white/[0.08] border border-white/15 px-6 py-4">
            <span className="block font-mono-tech text-[10px] uppercase tracking-widest text-emerald-300">{t("dd.startingAt")}</span>
            <span className="font-display font-extrabold text-3xl text-white">{d.priceFrom}<span className="text-lg text-white/60 font-semibold">{d.priceUnit || t("dd.perMo")}*</span></span>
          </div>
          <Link to={localePath("/quick-quote")} className="group inline-flex items-center gap-2 px-6 py-4 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold hover:bg-emerald-400 transition-all" data-testid="detail-quote-btn">
            {t("cta.requestQuote")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <p className="mt-3 text-[11px] text-white/45">{t("dd.priceFootnotePre")} {CONTACT.phone} {t("dd.priceFootnotePost")}</p>
      </PageHero>

      {/* Overview */}
      <section className="relative bg-[#FBFAF8] py-24 overflow-hidden">
        <span className="pointer-events-none select-none absolute inset-x-0 bottom-4 text-center font-display font-extrabold text-[26vw] lg:text-[18vw] text-[#0B0F14]/[0.035] leading-none tracking-tight">{d.watermark}</span>
        <div className="relative z-[2] max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <div className="bg-white rounded-3xl p-6 sm:p-10 border border-[#E7E5DF] shadow-soft grid place-items-center">
              <img loading="lazy" decoding="async" src={d.img} alt={`${d.title} — ${d.dimensions}`} className="w-full max-w-md object-contain" />
            </div>
          </motion.div>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionHeading eyebrow={t("dd.overview")} title={d.title} highlight={t("dd.rentalsWord2")} />
            <div className="mt-7 space-y-4">
              <div className="flex items-start gap-3">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-700 shrink-0"><Package size={18} /></span>
                <p className="text-[#0B0F14]"><span className="font-semibold">{t("dd.holds")}</span> {d.holds}</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-700 shrink-0"><Ruler size={18} /></span>
                <p className="text-[#0B0F14]"><span className="font-semibold">{t("dd.dimensions")}</span> {d.dimensions}</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-700 shrink-0"><BadgeDollarSign size={18} /></span>
                <p className="text-[#0B0F14]"><span className="font-semibold">{t("dd.startingAtLabel")}</span> {d.priceFrom} {d.priceNote || t("dd.perMonth")}</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-700 shrink-0"><Check size={18} /></span>
                <p className="text-[#0B0F14]"><span className="font-semibold">{t("dd.includes")}</span> {d.includes}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ideal For / What's Included */}
      <section className="relative bg-[#F0EFEA] py-24 overflow-hidden">
        <span className="pointer-events-none select-none absolute inset-x-0 top-1/3 text-center font-display font-extrabold text-[26vw] lg:text-[18vw] text-[#0B0F14]/[0.03] leading-none tracking-tight">{d.watermark}</span>
        <div className="relative z-[2] max-w-[1400px] mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-8">
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 sm:p-10 border border-[#E7E5DF] shadow-soft">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0B0F14] mb-6">{t("dd.idealFor")}</h2>
            <ul className="space-y-4">
              {d.idealFor.map((x) => (
                <li key={x} className="flex items-start gap-3 text-[#0B0F14]">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-emerald-600/10 text-emerald-700 shrink-0 mt-0.5"><Check size={13} strokeWidth={3} /></span>
                  {x}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="bg-[#0E3B3B] text-white rounded-3xl p-8 sm:p-10 shadow-soft">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl mb-6">{t("dd.whatsIncluded")}</h2>
            <ul className="space-y-4">
              {d.whatsIncluded.map((x) => (
                <li key={x} className="flex items-start gap-3 text-white/90">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 shrink-0 mt-0.5"><Check size={13} strokeWidth={3} /></span>
                  {x}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("dd.howEyebrow")} title={t("dd.howTitle")} highlight={t("dd.howHighlight")} className="mb-14" />
          <div className="grid md:grid-cols-3 gap-6">
            {PROCESS_STEPS.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <motion.div key={s.step} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
                  className="relative bg-white border border-[#E7E5DF] rounded-3xl p-8 shadow-soft">
                  <span className="absolute top-6 right-7 font-display font-extrabold text-5xl text-[#F0EFEA]">{s.step}</span>
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#0E3B3B] text-emerald-300 mb-5"><Icon size={24} /></span>
                  <h3 className="font-display font-bold text-lg text-[#0B0F14] max-w-[240px]">{s.title}</h3>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative bg-[#F0EFEA] py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("dd.faqEyebrow")} title={t("dd.faqTitle")} highlight={t("dd.faqHighlight")} className="mb-12" />
          <div className="space-y-3">
            {d.faqs.map((f, i) => (
              <div key={i} className="bg-white border border-[#E7E5DF] rounded-2xl overflow-hidden" data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left font-display font-bold text-[#0B0F14]">
                  {f.q}
                  <ChevronDown size={20} className={`shrink-0 text-emerald-700 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="px-5 pb-5 -mt-1 text-slate-600 text-sm leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other sizes — internal linking */}
      <section className="relative bg-[#FBFAF8] py-20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow={t("dd.compareEyebrow")} title={t("dd.compareTitle")} highlight={t("dd.compareHighlight")} className="mb-10" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {others.map((o) => (
              <Link key={o.slug} to={localePath(`${basePath}/${o.slug}`)} data-testid={`other-${o.slug}`}
                className="group bg-white border border-[#E7E5DF] rounded-2xl p-5 shadow-soft hover:-translate-y-1 hover:border-emerald-600/40 transition-all flex flex-col items-center text-center">
                <img loading="lazy" decoding="async" src={o.img} alt={o.title} className="h-24 object-contain mb-3" />
                <span className="font-display font-bold text-[#0B0F14] group-hover:text-emerald-700 transition-colors">{o.title}</span>
                <span className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500 mt-1">{t("common.from")} {o.priceFrom}{o.priceUnit || t("dd.perMo")}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
